import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCloudUploadAlt,
  faInfoCircle,
  faEllipsisV,
  faList
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { categoryApi } from 'api/endpoints/Category';
import apiClient from 'api/client';
import { getTenantFromToken } from 'utils/routeUtils';
import { formSubmissionToast } from 'helpers/utils';
import { FileDecoder } from 'api/FileUploadApi';

function CreateCategory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();
  const fileInputRef = useRef(null);

  // Form states
  const [categoryName, setCategoryName] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [categoryImage, setCategoryImage] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  // UI/API states
  const [parentCategories, setParentCategories] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch parent categories for dropdown (top-level only: Crop & Livestock)
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const response = await categoryApi.getCategoryList();
        console.log('getCategoryList API response:', response.data);
        if (response.data?.success) {
          let list = response.data.results || [];
          console.log('Unfiltered categories list:', list);
          // Only show top-level categories (Crop and Livestock)
          list = list.filter(cat => {
            const name = (cat.label || '').trim().toLowerCase();
            return name === 'crop' || name === 'crops' || name === 'livestock';
          });
          console.log('Filtered categories list (Crop & Livestock):', list);
          // Exclude self in edit mode
          if (isEditMode && id) {
            list = list.filter(cat => cat.value !== id);
          }
          setParentCategories(list);
        }
      } catch (error) {
        console.error('Error fetching parent categories:', error);
      }
    };
    fetchParentCategories();
  }, [id, isEditMode]);

  // Fetch existing category data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchCategoryDetails = async () => {
        try {
          const response = await categoryApi.getCategory(id);
          if (response.data?.success) {
            const cat = response.data.results || {};
            setCategoryName(cat.category_name || '');
            setParentCategoryId(cat.parent_category_id || '');
            setDescription(cat.description || '');
            setSortOrder(
              cat.sort_order !== undefined && cat.sort_order !== null
                ? String(cat.sort_order)
                : ''
            );

            setIsActive(cat.is_active !== false);
            // Store file_key for saving; fetch presigned URL for display
            setCategoryImage(cat.category_image || '');
            if (cat.category_image) {
              const presignedUrl = await FileDecoder(cat.category_image);
              setImageUrl(presignedUrl || cat.category_image || '');
            }
          }
        } catch (error) {
          console.error('Error fetching category details:', error);
          toast.error('Failed to load category details');
        }
      };
      fetchCategoryDetails();
    }
  }, [id, isEditMode]);

  // Handle value change dirty check
  const handleFieldChange = (setter, value) => {
    setter(value);
    setIsDirty(true);
  };

  // Image Upload handler
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation: max 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    // Allowed file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImageUploading(true);
      const response = await apiClient.post(
        '/digitization/v1/utility/upload/image',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const results = response.data?.results || {};
      setImageUrl(results.url || '');
      setCategoryImage(results.file_key || results.url || '');
      setIsDirty(true);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      const errMsg =
        error?.response?.data?.desc ||
        error?.response?.data?.message ||
        'Image upload failed. Please try again.';
      toast.error(errMsg);
    } finally {
      setImageUploading(false);
    }
  };

  // Submit Category
  const handleSubmit = async e => {
    if (e) e.preventDefault();
    if (!categoryName.trim()) {
      toast.error('Category Name is required');
      return;
    }
    if (!parentCategoryId) {
      toast.error('Parent Category is required');
      return;
    }

    const payload = {
      category_name: categoryName.trim(),
      parent_category_id: parentCategoryId || null,
      description: description.trim() || null,
      sort_order: parseInt(sortOrder) || 0,
      is_active: isActive,
      category_image: categoryImage || null
    };

    try {
      setSubmitting(true);
      let response;
      if (isEditMode) {
        response = await categoryApi.updateCategory(id, payload);
      } else {
        response = await categoryApi.createCategory(payload);
      }
      formSubmissionToast(response, toast, 'Category', isEditMode);
      setIsDirty(false);
      navigate(`/${tenant}/category-report`);
    } catch (error) {
      console.error('Error saving category:', error);
      formSubmissionToast(error, toast, 'Category', isEditMode);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      fluid
      className="px-4 py-3"
      style={{ backgroundColor: '#F8F9FC', minHeight: '100vh' }}
    >
      {/* Top Header Card */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body className="d-flex justify-content-between align-items-center py-2 px-3">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              onClick={() => navigate(`/${tenant}/category-report`)}
              className="text-secondary p-0 me-3 fs-5"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h5 className="mb-0 fw-bold" style={{ color: '#0A2540' }}>
              Product Management
            </h5>
          </div>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center px-3 fw-semi-bold btn-sm me-2"
              style={{ borderRadius: '6px', borderWidth: '1.5px' }}
              onClick={() => navigate(`/${tenant}/category-report`)}
            >
              <FontAwesomeIcon icon={faList} className="me-2" />
              View All Item Category
            </Button>
            <Button variant="link" className="text-secondary p-0 fs-5">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Main Title Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2
          className="mb-0 fw-bold"
          style={{ color: '#0A2540', fontSize: '24px' }}
        >
          {isEditMode ? 'Edit Category' : 'Create Category'}
        </h2>
        <div
          style={{ color: '#6C7A9C', fontSize: '14px' }}
          className="fw-semi-bold"
        >
          Category &gt;{' '}
          <span className="text-primary">{isEditMode ? 'Edit' : 'New'}</span>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4 mb-5">
          {/* Left Column - Form Inputs */}
          <Col lg={7} md={12}>
            <Card
              className="shadow-sm border-0 h-100"
              style={{ borderRadius: '12px' }}
            >
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Category Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Electronics, Home Appliances"
                    value={categoryName}
                    onChange={e =>
                      handleFieldChange(setCategoryName, e.target.value)
                    }
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Parent Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={parentCategoryId}
                    onChange={e =>
                      handleFieldChange(setParentCategoryId, e.target.value)
                    }
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  >
                    <option value="">Select Parent Category</option>
                    {parentCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Enter category details, usage, and shelf placement rules..."
                    value={description}
                    onChange={e =>
                      handleFieldChange(setDescription, e.target.value)
                    }
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>

                <Row className="g-3 align-items-end">
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Sort Order <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={sortOrder}
                        onChange={e =>
                          handleFieldChange(setSortOrder, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-0 ps-md-3">
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Category Status
                      </Form.Label>
                      <div className="d-flex align-items-center mt-2">
                        <Form.Check
                          type="switch"
                          id="category-status-switch"
                          checked={isActive}
                          onChange={e =>
                            handleFieldChange(setIsActive, e.target.checked)
                          }
                          className="fs-4 me-3"
                          style={{ cursor: 'pointer' }}
                        />
                        <span
                          className="fw-semi-bold"
                          style={{ color: '#0A2540', fontSize: '15px' }}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Category Image */}
          <Col lg={5} md={12}>
            <Card
              className="shadow-sm border-0"
              style={{ borderRadius: '12px' }}
            >
              <Card.Body className="p-4">
                <h6 className="fw-bold mb-3" style={{ color: '#0A2540' }}>
                  Category Image
                </h6>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/jpg"
                  style={{ display: 'none' }}
                />

                <div
                  onClick={triggerFileSelect}
                  className="d-flex flex-column align-items-center justify-content-center text-center p-4 mb-3"
                  style={{
                    border: '2px dashed #CBD5E1',
                    borderRadius: '8px',
                    backgroundColor: '#F8FAFC',
                    minHeight: '180px',
                    cursor: 'pointer'
                  }}
                >
                  {imageUploading ? (
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary mb-2"
                        role="status"
                        style={{ width: '2rem', height: '2rem' }}
                      >
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="text-muted small mb-0">
                        Uploading image, please wait...
                      </p>
                    </div>
                  ) : imageUrl ? (
                    <>
                      <img
                        src={imageUrl}
                        alt="Category Preview"
                        style={{
                          maxHeight: '150px',
                          maxWidth: '100%',
                          borderRadius: '6px',
                          objectFit: 'contain'
                        }}
                      />
                      <p
                        className="text-muted small mt-2 mb-0"
                        style={{ fontSize: '11px' }}
                      >
                        Click to replace image
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="mb-2">
                        <FontAwesomeIcon
                          icon={faCloudUploadAlt}
                          size="2x"
                          className="text-muted mb-2"
                        />
                      </div>
                      <p
                        className="mb-1 fw-semi-bold text-primary"
                        style={{ fontSize: '14px' }}
                      >
                        Click to upload or drag &amp; drop
                      </p>
                      <p className="text-muted small mb-0">
                        PNG, JPG up to 5MB
                      </p>
                    </>
                  )}
                </div>

                <div
                  className="d-flex align-items-start p-3 bg-light"
                  style={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-info mt-1 me-2"
                  />
                  <p
                    className="mb-0 small text-secondary"
                    style={{ lineHeight: '1.4' }}
                  >
                    Images are used for POS visual navigation and
                    customer-facing catalogs.
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Footer Bar */}
        <div
          className="fixed-bottom d-flex justify-content-between align-items-center px-4 py-3 bg-white border-top shadow-lg"
          style={{ zIndex: 1020 }}
        >
          <div>
            {isDirty && (
              <span className="small text-danger fw-bold">
                Unsaved changes in &quot;
                {isEditMode ? 'Edit Category' : 'New Category'}&quot;
              </span>
            )}
          </div>
          <div>
            <Button
              variant="outline-secondary"
              className="me-3 px-4 fw-semi-bold btn-sm"
              style={{ borderRadius: '6px' }}
              onClick={() => navigate(`/${tenant}/category-report`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="px-4 fw-semi-bold btn-sm"
              style={{
                borderRadius: '6px',
                backgroundColor: '#0048A2',
                borderColor: '#0048A2'
              }}
            >
              {submitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                ? 'Update Category'
                : 'Create Category'}
            </Button>
          </div>
        </div>
      </Form>
    </Container>
  );
}

export default CreateCategory;
